import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, ReferenceLine } from 'recharts';
import { Spin, Alert, Space } from 'antd';
import styles from '../styles/WearerBatteryHistory.module.css';
import sharedStyles from '../styles/Common.module.css';
import { avisosDeCobertura, formatearFechaCorta, resumenLecturas } from '../utils/coberturaBateria';

// Un estilo por cada veredicto del backend. Los dos grises no son "batería
// mala": son ausencia de evidencia, y no deben alarmar a soporte.
const ESTADOS = {
  BUENO: { dot: styles.dotBueno, badge: styles.badgeBueno },
  REGULAR: { dot: styles.dotRegular, badge: styles.badgeRegular },
  MALO: { dot: styles.dotMalo, badge: styles.badgeMalo },
  DATOS_INSUFICIENTES: { dot: styles.dotGris, badge: styles.badgeGris },
  SIN_TELEMETRIA: { dot: styles.dotGris, badge: styles.badgeGris },
};

// Solo estos estados traen un veredicto. Sin él el backend igual puede calcular
// una autonomía, pero mostrarla como "típica" sería afirmar lo que el propio
// diagnóstico dice que no se puede afirmar.
const CON_VEREDICTO = ['BUENO', 'REGULAR', 'MALO'];

const formatearFechaHora = (ms) => {
  const date = new Date(ms);
  const dia = date.getDate().toString().padStart(2, '0');
  const mes = (date.getMonth() + 1).toString().padStart(2, '0');
  const horas = date.getHours().toString().padStart(2, '0');
  const minutos = date.getMinutes().toString().padStart(2, '0');
  return `${dia}/${mes} ${horas}:${minutos}`;
};

const formatearDuracion = (minutos) => {
  if (!minutos) return '—';
  if (minutos < 60) return `${Math.round(minutos)} min`;
  const horas = minutos / 60;
  return horas < 48 ? `${horas.toFixed(1)} h` : `${(horas / 24).toFixed(1)} días`;
};

/** Texto del badge: la autonomía si hay veredicto, y si no, qué falta para tenerlo. */
const textoBadge = (diagnostico, cobertura) => {
  if (CON_VEREDICTO.includes(diagnostico.estado)) {
    return `${diagnostico.autonomiaMedianaH} h de autonomía típica`;
  }
  if (diagnostico.estado === 'SIN_TELEMETRIA') return 'sin lecturas';
  const { sesionesValidas, criterio } = diagnostico;
  if (sesionesValidas < criterio.minSesiones) {
    return `${sesionesValidas} de ${criterio.minSesiones} descargas necesarias`;
  }
  // Tiene las descargas pero concentradas en muy pocos días.
  return `${sesionesValidas} descargas en solo ${cobertura ? cobertura.diasObservados : '—'} d de telemetría`;
};

/** Título y bajada del recuadro que ocupa el lugar del gráfico cuando no hay serie. */
const textoSinGrafico = ({ loading, error, consultado }) => {
  if (loading) return ['Consultando…', 'Puede tardar unos segundos.'];
  // Tras un error no se sabe si el reloj reportó: decir que no lo hizo
  // contradiría el aviso de arriba.
  if (error) return ['Sin gráfico', 'La consulta falló, así que no hay datos que dibujar.'];
  if (consultado) {
    return [
      'El reloj no reportó batería en los últimos días',
      'Puede estar apagado o sin señal. Vuelve a intentarlo más adelante antes de escalar el caso.',
    ];
  }
  return ['Sin diagnóstico todavía', 'Pulsa Diagnosticar, arriba a la derecha, para analizar la batería.'];
};

export default function WearerBatteryHistory({
  diagnostico,
  serie,
  cobertura,
  sesiones,
  loading,
  error,
  consultado,
  diagnosticar,
}) {
  const estilosEstado = diagnostico ? ESTADOS[diagnostico.estado] : null;
  const conVeredicto = diagnostico && CON_VEREDICTO.includes(diagnostico.estado);
  const hayGrafico = serie && serie.length > 0;
  const haySesiones = sesiones && sesiones.length > 0;
  const avisos = avisosDeCobertura(cobertura);
  const resumen = resumenLecturas(cobertura);
  const [tituloVacio, bajadaVacio] = textoSinGrafico({ loading, error, consultado });

  return (
    <div className={styles.generalContainer}>
      {/* Encabezado. La acción va aquí arriba a la derecha, en el mismo lugar y
          con la misma forma que el botón de recargar del resto de las tarjetas. */}
      <div className={sharedStyles.cardSubContainer}>
        <div className={sharedStyles.flexCenter}>
          <div className={styles.iconContainer}>
            <img src="/images/cs-batteryIcon.svg" width={24} height={24} alt="" />
          </div>
          <div className={styles.textContainer}>
            <h1 className={styles.title}>Diagnóstico de batería</h1>
            <p className={styles.subtitle}>Baterías</p>
          </div>
        </div>
        <button type="button" className={styles.accion} onClick={() => diagnosticar()} disabled={loading}>
          <img
            src="/images/tableIcons/cs-refreshIcon.svg"
            className={styles.accionIcono}
            alt=""
          />
          {loading ? 'Diagnosticando…' : 'Diagnosticar'}
        </button>
      </div>

      {error && (
        <div className={styles.bloque}>
          <Alert message="No se pudo diagnosticar" description={error} type="error" showIcon />
        </div>
      )}

      {/* Resultado */}
      {diagnostico && !error && (
        <div className={styles.panel}>
          <div className={styles.panelCabecera}>
            <span className={styles.estado}>
              <i className={`${styles.dot} ${estilosEstado.dot}`} aria-hidden="true" />
              {diagnostico.titulo}
            </span>
            <span className={estilosEstado.badge}>{textoBadge(diagnostico, cobertura)}</span>
          </div>

          <p className={styles.recomendacion}>{diagnostico.recomendacion}</p>

          <div className={styles.metricas}>
            <div className={styles.metrica}>
              <span className={styles.metricaValor}>{diagnostico.sesionesValidas}</span>
              <span className={styles.metricaEtiqueta}>descargas analizadas</span>
            </div>
            <div className={styles.metrica}>
              <span className={styles.metricaValor}>
                {diagnostico.autonomiaPeorCicloH !== null ? `${diagnostico.autonomiaPeorCicloH} h` : '—'}
              </span>
              <span className={styles.metricaEtiqueta}>su peor descarga</span>
            </div>
            <div className={styles.metrica}>
              <span className={styles.metricaValor}>
                {cobertura ? `${cobertura.diasObservados} d` : '—'}
              </span>
              <span className={styles.metricaEtiqueta}>con telemetría real</span>
            </div>
          </div>

          <p className={styles.muted}>
            Autonomía típica: horas que duraría de 100% a 0% al ritmo de sus descargas habituales. Buena
            desde {diagnostico.criterio.buenoH} h, mala bajo {diagnostico.criterio.maloH} h; para opinar
            hacen falta al menos {diagnostico.criterio.minSesiones} descargas repartidas en varios días.
            {conVeredicto && diagnostico.cumpleObjetivoComercial !== null && (
              <>
                {' '}Objetivo comercial de {diagnostico.criterio.objetivoComercialH} h:{' '}
                {diagnostico.cumpleObjetivoComercial ? 'lo cumple' : 'no lo cumple'}.
              </>
            )}
          </p>

          {cobertura && cobertura.lecturas > 0 && (
            <p className={styles.muted}>
              Datos entre {formatearFechaCorta(cobertura.desde)} y {formatearFechaCorta(cobertura.hasta)}
              {cobertura.huecos > 0 && (
                <>
                  {' '}· {cobertura.huecos} {cobertura.huecos === 1 ? 'corte' : 'cortes'} de telemetría,
                  el mayor de {formatearDuracion(cobertura.huecoMayorMin)}
                </>
              )}
            </p>
          )}

          {/* Todos los avisos que apliquen: cada uno describe un hueco distinto
              en los datos, y ocultar uno detrás de otro hacía confiar de más. */}
          {avisos.length > 0 && (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {avisos.map((aviso) => (
                <Alert
                  key={aviso.clave}
                  type="warning"
                  showIcon
                  message={aviso.message}
                  description={aviso.description}
                />
              ))}
            </Space>
          )}
        </div>
      )}

      {/* Gráfico */}
      <Spin spinning={loading} tip="Consultando la telemetría del reloj…">
        {hayGrafico ? (
          <div className={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={serie} margin={{ top: 8, right: 16, bottom: 4, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                {/* Eje temporal real: con el eje categórico anterior, dos lecturas
                    separadas por horas se dibujaban pegadas y el hueco no se veía. */}
                <XAxis
                  dataKey="t"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatearFechaHora}
                  minTickGap={40}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip
                  labelFormatter={formatearFechaHora}
                  formatter={(value) => [`${value}%`, 'Nivel de batería']}
                />
                <ReferenceLine y={20} stroke="#d18f00" strokeDasharray="4 4" />
                <Line
                  dataKey="b"
                  stroke="#603BB0"
                  name="Nivel de batería (%)"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className={styles.muted}>
              La línea se corta donde el reloj dejó de reportar. La línea punteada marca el 20%.
            </p>
          </div>
        ) : (
          <div className={styles.vacio}>
            <img
              src="/images/cs-batteryIcon.svg"
              width={48}
              height={48}
              alt=""
              className={styles.vacioIcono}
            />
            <p className={styles.vacioTitulo}>{tituloVacio}</p>
            <p className={styles.muted}>{bajadaVacio}</p>
          </div>
        )}
      </Spin>

      {/* Lo que sostiene el veredicto, plegado: soporte no lo necesita para actuar */}
      {diagnostico && !error && (haySesiones || resumen) && (
        <details className={styles.detalle}>
          <summary className={styles.detalleResumen}>
            {haySesiones ? `Ver las ${sesiones.length} descargas analizadas` : 'Ver detalle de los datos'}
          </summary>
          {resumen && <p className={`${styles.muted} ${styles.bloque}`}>{resumen}.</p>}
          {haySesiones && (
            <div className={styles.tablaContenedor}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th>Inicio</th>
                    <th>Duración</th>
                    <th>Caída</th>
                    <th>Autonomía</th>
                  </tr>
                </thead>
                <tbody>
                  {sesiones.map((s) => (
                    <tr key={s.inicio}>
                      <td>{formatearFechaHora(new Date(s.inicio).getTime())}</td>
                      <td>{s.duracionH} h</td>
                      <td>
                        {s.nivelInicial}% → {s.nivelFinal}%
                      </td>
                      <td>{s.autonomiaH} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      )}
    </div>
  );
}
